import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import {
  PROMO_LINE_PLACEHOLDER_CLASS,
  REAL_PROMO_LINE_SELECTOR,
  buildPromoLineHtml,
  parseListAttr,
  readPromoRules,
  resolvePromo,
} from '@theme/promo-rules';

const SWATCH_LINK_SELECTOR = '.product-recommendations__swatch-link[data-product-card-swatch]';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #fitPaginationSegments = [];
  #fitQuickShopButtons = [];
  #fitQuickShopLoadingPoll = null;
  #fitQuickShopLoadingTimeout = null;
  #prefetchTimer = null;
  /**
   * Markup of the invisible line this card keeps in its price block whenever
   * the displayed colour has no offer but some colour on the card does: the
   * widest candidate's line, rendered as a placeholder so the swatch row and
   * the low-stock line stay put while a hover toggles the real line on and
   * off. Empty when no colour on the card resolves to an offer.
   * @type {string}
   */
  #promoReserveHtml = '';
  static breadcrumbTitleParam = 'breadcrumb_title';
  static breadcrumbUrlParam = 'breadcrumb_url';
  static breadcrumbChildTitleParam = 'breadcrumb_child_title';
  static breadcrumbChildUrlParam = 'breadcrumb_child_url';
  static breadcrumbCurrentTitleParam = 'breadcrumb_current_title';

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  // Debounce Quick Add prefetch so that sweeping the mouse across a grid of
  // product cards doesn't fire a fetch per card (which was triggering 429s
  // from Shopify's storefront rate limit on PLPs with 40+ cards). The fetch
  // only runs if the pointer/focus stays on the card for ~300ms — long enough
  // to feel instant on deliberate hover, short enough to skip drive-bys.
  #fetchProductPageHandler = () => {
    if (this.#prefetchTimer) clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = setTimeout(() => {
      this.refs.quickAdd?.fetchProductPage?.(this.productPageUrl);
      this.#prefetchTimer = null;
    }, 300);
  };

  #cancelFetchProductPagePrefetch = () => {
    if (this.#prefetchTimer) {
      clearTimeout(this.#prefetchTimer);
      this.#prefetchTimer = null;
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.syncBreadcrumbHref(link);
    this.syncBreadcrumbHref(this.refs.productTitleLink);
    this.syncBreadcrumbHref(this.refs.cardGalleryLink);
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.#handleRecommendationSwatchClick);
    this.addEventListener('pointerover', this.#handleRecommendationSwatchHover);
    this.addEventListener('pointerout', this.#handleRecommendationSwatchHoverOut);
    this.addEventListener('focusin', this.#handleRecommendationSwatchFocus);
    this.addEventListener('focusout', this.#handleRecommendationSwatchFocusOut);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('click', this.navigateToProduct);
    this.#setupFitPaginationSegments();
    this.#setupFitQuickShopButtons();
    this.#computePromoReserve();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupFitPaginationSegments();
    this.#cleanupFitQuickShopButtons();
    this.#setFitQuickShopLoading(false);
    if (this.#prefetchTimer) {
      clearTimeout(this.#prefetchTimer);
      this.#prefetchTimer = null;
    }
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.removeEventListener('click', this.#handleRecommendationSwatchClick);
    this.removeEventListener('pointerover', this.#handleRecommendationSwatchHover);
    this.removeEventListener('pointerout', this.#handleRecommendationSwatchHoverOut);
    this.removeEventListener('focusin', this.#handleRecommendationSwatchFocus);
    this.removeEventListener('focusout', this.#handleRecommendationSwatchFocusOut);
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    this.removeEventListener('pointerleave', this.#cancelFetchProductPagePrefetch);
    this.removeEventListener('focusout', this.#cancelFetchProductPagePrefetch);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('click', this.navigateToProduct);
  }

  #handleRecommendationSwatchClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('.product-recommendations__swatch-link[data-product-card-swatch]');
    if (!(swatchLink instanceof HTMLAnchorElement) || !this.contains(swatchLink)) return;

    event.preventDefault();
    event.stopPropagation();

    this.#applyRecommendationSwatchState(swatchLink);
  };

  #handleRecommendationSwatchHover = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('.product-recommendations__swatch-link[data-product-card-swatch]');
    if (!(swatchLink instanceof HTMLAnchorElement) || !this.contains(swatchLink)) return;

    this.#holdPriceHeight();
    this.#applyRecommendationSwatchState(swatchLink, { commitActive: false, fetchQuickAdd: false });
    this.#holdPriceHeight();
  };

  #holdPriceHeight() {
    this.querySelectorAll('product-price').forEach((price) => {
      if (!(price instanceof HTMLElement)) return;
      const height = price.getBoundingClientRect().height;
      if (height > (parseFloat(price.style.minHeight) || 0)) price.style.minHeight = `${height}px`;
    });
  }

  #releasePriceHeight() {
    this.querySelectorAll('product-price').forEach((price) => {
      if (price instanceof HTMLElement) price.style.minHeight = '';
    });
  }

  #handleRecommendationSwatchHoverOut = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('.product-recommendations__swatch-link[data-product-card-swatch]');
    if (!(swatchLink instanceof HTMLAnchorElement) || !this.contains(swatchLink)) return;

    const nextSwatchLink =
      event.relatedTarget instanceof Element
        ? event.relatedTarget.closest('.product-recommendations__swatch-link[data-product-card-swatch]')
        : null;

    if (nextSwatchLink instanceof HTMLAnchorElement && this.contains(nextSwatchLink)) return;

    this.#restoreActiveRecommendationSwatchState();
  };

  #handleRecommendationSwatchFocus = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('.product-recommendations__swatch-link[data-product-card-swatch]');
    if (!(swatchLink instanceof HTMLAnchorElement) || !this.contains(swatchLink)) return;

    this.#applyRecommendationSwatchState(swatchLink, { commitActive: false, fetchQuickAdd: false });
  };

  #handleRecommendationSwatchFocusOut = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('.product-recommendations__swatch-link[data-product-card-swatch]');
    if (!(swatchLink instanceof HTMLAnchorElement) || !this.contains(swatchLink)) return;

    const nextSwatchLink =
      event.relatedTarget instanceof Element
        ? event.relatedTarget.closest('.product-recommendations__swatch-link[data-product-card-swatch]')
        : null;

    if (nextSwatchLink instanceof HTMLAnchorElement && this.contains(nextSwatchLink)) return;

    this.#restoreActiveRecommendationSwatchState();
  };

  #handlePointerLeave = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
    this.#releasePriceHeight();
    if (this.hasAttribute('quick-add-active')) return;

    this.resetVariant.cancel();
    this.#previousSlideIndex = null;

    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      this.contains(activeElement) &&
      activeElement.closest('.card-gallery, .quick-add') &&
      activeElement.matches('.slideshow-control, .quick-add__button, .quick-add__button *')
    ) {
      activeElement.blur();
    }

    this.#resetVariant();
  };

  #restoreActiveRecommendationSwatchState() {
    const activeSwatch = this.querySelector('.product-recommendations__swatch-link.is-active[data-product-card-swatch]');
    if (!(activeSwatch instanceof HTMLAnchorElement)) return;

    this.#applyRecommendationSwatchState(activeSwatch, { commitActive: false, fetchQuickAdd: false });
  }

  #applyRecommendationSwatchState(swatchLink, options = {}) {
    const { commitActive = true, fetchQuickAdd = true } = options;
    const nextProductUrl = this.withBreadcrumbParams(swatchLink.dataset.productUrl || swatchLink.href);
    const nextTitleText = swatchLink.dataset.titleText || '';
    const nextSubtitleText = swatchLink.dataset.subtitleText || '';
    const nextImageUrl = swatchLink.dataset.imageUrl || '';
    const nextImageAlt = swatchLink.dataset.imageAlt || nextTitleText;
    const nextPriceHtml = swatchLink.dataset.priceHtml || '';
    // Offer scope for the "$X with code" line: the sibling product's tags,
    // stamped by snippets/product-card.liquid. null (attribute absent) means a
    // Constructor-built link, whose data-price-html is already final.
    const nextProductTags =
      swatchLink.dataset.productTags != null ? parseListAttr(swatchLink.dataset.productTags) : null;
    const nextMediaJson = swatchLink.dataset.mediaJson || '';

    if (nextProductUrl) {
      this.syncProductUrls(nextProductUrl);

      const { cardGalleryLink } = this.refs;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        if (nextTitleText) {
          cardGalleryLink.setAttribute('aria-label', nextTitleText);
        }
      }
    }

    if (nextTitleText) {
      this.#updateRecommendationTitle(nextTitleText);
      this.refs.quickAdd?.setAttribute('data-product-title', nextTitleText);
    }
    this.#updateRecommendationSubtitle(nextSubtitleText);

    const nextMedia = this.#parseRecommendationMedia(nextMediaJson);
    if (nextMedia.length) {
      this.#updateRecommendationGallery(nextMedia, nextImageAlt || nextTitleText);
    } else if (nextImageUrl) {
      this.#updateRecommendationImage(nextImageUrl, nextImageAlt || nextTitleText);
    }

    if (nextPriceHtml) {
      this.#updateRecommendationPrice(nextPriceHtml, nextProductTags);
    }

    if (commitActive) {
      this.querySelectorAll('.product-recommendations__swatch-link[data-product-card-swatch]').forEach((link) => {
        const isActive = link === swatchLink;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    if (fetchQuickAdd) {
      this.refs.quickAdd?.fetchProductPage(nextProductUrl);
    }
  }

  #parseRecommendationMedia(mediaJson) {
    if (!mediaJson) return [];

    try {
      const media = JSON.parse(mediaJson);
      return Array.isArray(media) ? media : [];
    } catch {
      return [];
    }
  }

  #updateRecommendationTitle(titleText) {
    const { productCardLink, productTitleLink } = this.refs;
    const formattedTitleText = this.#formatProductCardTitle(titleText);

    const cardLabel = productCardLink.querySelector('.visually-hidden');
    if (cardLabel) {
      cardLabel.textContent = formattedTitleText;
    }

    if (!(productTitleLink instanceof HTMLAnchorElement)) return;

    const titleTarget =
      productTitleLink.querySelector('.title-text, p, span, h1, h2, h3, h4, h5, h6') || productTitleLink;
    titleTarget.textContent = formattedTitleText;
    productTitleLink.setAttribute('aria-label', formattedTitleText);
  }

  #updateRecommendationSubtitle(subtitleText) {
    this.querySelectorAll('.custom-product-subtitle').forEach((subtitle) => {
      subtitle.textContent = subtitleText;
    });
  }

  #formatProductCardTitle(text) {
    const baseTitle = text || '';
    return baseTitle
      .split(' ')
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  #updateRecommendationPrice(priceHtml, productTags = null) {
    this.querySelectorAll(`product-price [ref='priceContainer']`).forEach((priceContainer) => {
      priceContainer.innerHTML = this.#buildRecommendationPriceHtml(priceContainer, priceHtml, productTags);
    });
  }

  #buildRecommendationPriceHtml(currentPriceContainer, nextPriceHtml, productTags = null) {
    const {
      markup: nextMarkup,
      priceElement: nextPriceElement,
      priceValue: nextPriceValue,
    } = this.#parseTilePriceHtml(nextPriceHtml);

    // Real lines only: the placeholder has no offer behind it, so it must not
    // count as one here.
    const nextDiscountLine = nextMarkup.querySelector(REAL_PROMO_LINE_SELECTOR);

    // Constructor-built swatch rows ship data-price-html WITH the line already
    // resolved per colourway (from each colourway's own Constructor record);
    // Liquid card swatches (snippets/product-card.liquid) ship the bare price
    // plus data-product-tags, so resolve the offer here from the page's rules
    // — the same resolver product-price.js uses. A link with neither keeps
    // the bare price: the card's own offer is never re-applied to a sibling,
    // since colourways of one listing can be tagged for different offers.
    if (!nextDiscountLine) {
      const rules = readPromoRules();

      if (nextPriceValue > 0 && rules.length) {
        const promo = this.#resolveTilePromo(nextPriceValue, productTags, rules);
        if (promo) {
          // The tile's own price text carries the money format (prefix,
          // suffix); #formatPromoAmount decides the cents from the amount.
          const sample = nextPriceElement?.textContent || '$0';
          nextMarkup.insertAdjacentHTML(
            'beforeend',
            buildPromoLineHtml(promo, this.#formatPromoAmount(sample, promo.discountedCents))
          );
        }
      }
    }

    // No real line for this colour: hold its row with the card's reserved
    // placeholder so the swatches and low-stock line below do not move.
    if (this.#promoReserveHtml && !nextMarkup.querySelector(REAL_PROMO_LINE_SELECTOR)) {
      nextMarkup.querySelector('.price-group-wrapper')?.insertAdjacentHTML('afterend', this.#promoReserveHtml);
    }

    return nextMarkup.innerHTML;
  }

  /**
   * A swatch link's data-price-html as a fragment with the price inside a
   * .price-group-wrapper (the Liquid tile markup ships it bare), plus the
   * element and numeric value of the price it carries.
   * @param {string} priceHtml
   * @returns {{ markup: HTMLDivElement, priceElement: Element | null, priceValue: number }}
   */
  #parseTilePriceHtml(priceHtml) {
    const markup = document.createElement('div');
    markup.innerHTML = priceHtml;

    if (!markup.querySelector('.price-group-wrapper')) {
      const wrapper = document.createElement('span');
      wrapper.className = 'price-group-wrapper';

      while (markup.firstChild) {
        wrapper.append(markup.firstChild);
      }

      markup.append(wrapper);
    }

    const priceElement = markup.querySelector('.price.custom-price--highlight, .price-group-wrapper > .price');
    return { markup, priceElement, priceValue: this.#extractPriceValue(priceElement) };
  }

  /**
   * The offer a tile price gets from the page's rules, or null. `productTags`
   * null means the link carries no data-product-tags (a Constructor-built
   * link), so no offer is resolved here: its data-price-html is final.
   * @param {number} priceValue
   * @param {string[] | null} productTags
   * @param {import('@theme/promo-rules').PromoRule[]} rules
   */
  #resolveTilePromo(priceValue, productTags, rules) {
    if (!(priceValue > 0) || !rules.length || !productTags) return null;
    return resolvePromo(rules, { priceCents: Math.round(priceValue * 100), tags: productTags });
  }

  /**
   * The offer one swatch link's colour gets, from the attributes
   * snippets/product-card.liquid stamps on it (data-price-html,
   * data-product-tags), with the price text to format the amount from. Null
   * for an unpriced link or one without tags.
   * @param {HTMLElement} swatchLink
   * @param {import('@theme/promo-rules').PromoRule[]} rules
   * @returns {{ promo: import('@theme/promo-rules').PromoResolution, sample: string } | null}
   */
  #resolveSwatchLinkPromo(swatchLink, rules) {
    const productTags = swatchLink.dataset.productTags != null ? parseListAttr(swatchLink.dataset.productTags) : null;
    const { priceElement, priceValue } = this.#parseTilePriceHtml(swatchLink.dataset.priceHtml || '');
    const promo = this.#resolveTilePromo(priceValue, productTags, rules);
    if (!promo) return null;
    return { promo, sample: priceElement?.textContent || '$0' };
  }

  /**
   * Decide, once per connect, which offer line this card reserves space for.
   *
   * Candidates are every colour the card can show: each swatch link's own
   * offer plus the real line the server rendered for the displayed colour.
   * The one with the longest rendered text becomes the placeholder markup
   * (#promoReserveHtml); when the displayed colour has no real line, that
   * placeholder goes into the price block right away, so hovering a promo
   * colour swaps text in place rather than pushing the swatch row down. Cards
   * where no colour has an offer keep an empty reserve and are unchanged.
   *
   * Reserved at hydration rather than SSR because blocks/price.liquid renders
   * the price with only `closest.product`, before the snippet builds the
   * swatch list — the one accepted shift is on a mixed card whose default
   * colour has no offer, once, on connect.
   */
  #computePromoReserve() {
    this.#promoReserveHtml = '';
    const rules = readPromoRules();
    if (!rules.length) return;

    const container = this.querySelector(`product-price [ref='priceContainer']`);
    if (!(container instanceof HTMLElement) || container.querySelector('.price-range')) return;
    // A re-connect must not stack a second ghost on top of an earlier one.
    container.querySelectorAll(`.${PROMO_LINE_PLACEHOLDER_CLASS}`).forEach((ghost) => ghost.remove());

    let bestHtml = '';
    let bestLength = -1;
    const consider = (html, text) => {
      const length = text.replace(/\s+/g, ' ').trim().length;
      if (length > bestLength) {
        bestHtml = html;
        bestLength = length;
      }
    };

    const ssrLine = container.querySelector(REAL_PROMO_LINE_SELECTOR);
    if (ssrLine instanceof HTMLElement) {
      const ghost = /** @type {HTMLElement} */ (ssrLine.cloneNode(true));
      ghost.classList.add(PROMO_LINE_PLACEHOLDER_CLASS);
      ghost.setAttribute('aria-hidden', 'true');
      ghost.removeAttribute('hidden');
      ghost.style.removeProperty('display');
      for (const name of Object.keys(ghost.dataset)) {
        if (name.startsWith('promo')) delete ghost.dataset[name];
      }
      consider(ghost.outerHTML, ssrLine.textContent || '');
    }

    this.querySelectorAll(SWATCH_LINK_SELECTOR).forEach((swatchLink) => {
      if (!(swatchLink instanceof HTMLElement)) return;
      const resolved = this.#resolveSwatchLinkPromo(swatchLink, rules);
      if (!resolved) return;
      const { promo, sample } = resolved;
      const amount = this.#formatPromoAmount(sample, promo.discountedCents);
      const text = (promo.hidePrice ? '' : `${amount} `) + String(promo.message ?? '').replace('%code%', promo.code);
      consider(buildPromoLineHtml(promo, amount, { placeholder: true }), text);
    });

    if (!bestHtml) return;
    this.#promoReserveHtml = bestHtml;

    if (!ssrLine) {
      container.querySelector('.price-group-wrapper')?.insertAdjacentHTML('afterend', bestHtml);
    }
  }

  #extractPriceValue(element) {
    const text = element?.textContent?.trim();
    if (!text) return 0;

    const normalized = text.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Money-format a cents amount with the prefix/suffix of a sample price text
   * ("$68", "$68.50 USD"), showing cents only when there are any — the same
   * rule product-price.js #formatMoney applies.
   */
  #formatPromoAmount(sampleText, cents) {
    const sample = String(sampleText || '').trim();
    const prefix = sample.match(/^[^0-9-]*/)?.[0] || '';
    const suffix = sample.match(/[^0-9]*$/)?.[0] || '';
    const value = cents / 100;
    return `${prefix}${cents % 100 === 0 ? value.toFixed(0) : value.toFixed(2)}${suffix}`;
  }


  #updateRecommendationImage(imageUrl, imageAlt) {
    const visibleImages = Array.from(this.querySelectorAll('.card-gallery img')).filter(
      (image) => image instanceof HTMLImageElement && image.closest('[hidden]') == null && image.hidden === false
    );
    const allImages = Array.from(this.querySelectorAll('.card-gallery img')).filter(
      (image) => image instanceof HTMLImageElement
    );
    const targetImage = visibleImages[0] || allImages[0];

    if (targetImage instanceof HTMLImageElement) {
      targetImage.src = imageUrl;
      targetImage.removeAttribute('srcset');
      targetImage.alt = imageAlt;
    }

    if (imageAlt) {
      allImages.forEach((image) => {
        if (image instanceof HTMLImageElement && image === targetImage) return;
        if (image instanceof HTMLImageElement && !image.getAttribute('alt')) {
          image.alt = imageAlt;
        }
      });
    }

    if (this.closest('product-card-link')) {
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', imageUrl);
    }
  }

  #updateRecommendationGallery(mediaItems, fallbackAlt) {
    const slides = Array.from(this.querySelectorAll('slideshow-slide[ref="slides[]"]'));

    if (!slides.length) {
      const firstMedia = mediaItems[0];
      if (firstMedia?.src) {
        this.#updateRecommendationImage(firstMedia.src, firstMedia.alt || fallbackAlt);
      }
      return;
    }

    slides.forEach((slide, index) => {
      const media = mediaItems[index];
      const image = slide.querySelector('img.product-media__image');
      const mediaContainer = slide.querySelector('.product-media');

      if (!media || !(image instanceof HTMLImageElement)) {
        slide.hidden = true;
        slide.setAttribute('aria-hidden', 'true');
        return;
      }

      slide.hidden = false;
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      if (media.id != null) {
        slide.setAttribute('slide-id', `${media.id}`);
        if (mediaContainer instanceof HTMLElement) {
          mediaContainer.dataset.mediaId = `${media.id}`;
        }
      }

      image.src = media.src;
      image.removeAttribute('srcset');
      image.alt = media.alt || fallbackAlt;
    });

    const firstMedia = mediaItems[0];
    if (firstMedia?.src && this.closest('product-card-link')) {
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', firstMedia.src);
    }

    this.#previousSlideIndex = null;
    this.#applyMobileSlideLimit();
    this.refs.slideshow?.select?.(0, undefined, { animate: false });
    this.#syncFitPaginationWithSlides(0);
  }

  #setupFitPaginationSegments() {
    this.#fitPaginationSegments = Array.from(this.querySelectorAll('[data-fit-pagination-segment]'));
    this.#fitPaginationSegments.forEach((segment) => {
      segment.addEventListener('click', this.#handleFitPaginationSegmentClick);
    });
  }

  #cleanupFitPaginationSegments() {
    this.#fitPaginationSegments.forEach((segment) => {
      segment.removeEventListener('click', this.#handleFitPaginationSegmentClick);
    });
    this.#fitPaginationSegments = [];
  }

  #setupFitQuickShopButtons() {
    this.#fitQuickShopButtons = Array.from(this.querySelectorAll('[data-fit-quick-shop]'));
    this.#fitQuickShopButtons.forEach((button) => {
      button.addEventListener('click', this.#handleFitQuickShopClick);
    });
  }

  #cleanupFitQuickShopButtons() {
    this.#fitQuickShopButtons.forEach((button) => {
      button.removeEventListener('click', this.#handleFitQuickShopClick);
    });
    this.#fitQuickShopButtons = [];
  }

  #handleFitQuickShopClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const quickAdd = /** @type {import('./quick-add').QuickAddComponent | null} */ (
      this.querySelector('quick-add-component')
    );
    if (!quickAdd) return;

    this.#setFitQuickShopLoading(true);

    if (typeof quickAdd.handleClick !== 'function') {
      this.#setFitQuickShopLoading(false);
      return;
    }

    quickAdd.handleClick(new Event('click', { cancelable: true }));
    this.#waitForQuickAddDialog();
  };

  #waitForQuickAddDialog() {
    const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('#quick-add-dialog dialog'));
    if (!dialog) {
      this.#setFitQuickShopLoading(false);
      return;
    }

    if (this.#fitQuickShopLoadingPoll) {
      clearInterval(this.#fitQuickShopLoadingPoll);
      this.#fitQuickShopLoadingPoll = null;
    }

    this.#fitQuickShopLoadingPoll = window.setInterval(() => {
      if (dialog.hasAttribute('open')) {
        this.#setFitQuickShopLoading(false);
      }
    }, 60);

    if (this.#fitQuickShopLoadingTimeout) {
      clearTimeout(this.#fitQuickShopLoadingTimeout);
    }
    this.#fitQuickShopLoadingTimeout = window.setTimeout(() => {
      this.#setFitQuickShopLoading(false);
    }, 7000);
  }

  #setFitQuickShopLoading(isLoading) {
    this.classList.toggle('is-fit-quick-shop-loading', isLoading);
    this.#fitQuickShopButtons.forEach((button) => {
      button.disabled = isLoading;
    });

    if (!isLoading) {
      if (this.#fitQuickShopLoadingPoll) {
        clearInterval(this.#fitQuickShopLoadingPoll);
        this.#fitQuickShopLoadingPoll = null;
      }
      if (this.#fitQuickShopLoadingTimeout) {
        clearTimeout(this.#fitQuickShopLoadingTimeout);
        this.#fitQuickShopLoadingTimeout = null;
      }
    }
  }

  #handleFitPaginationSegmentClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const selectedSegment = /** @type {HTMLButtonElement | null} */ (event.currentTarget);
    if (!selectedSegment) return;

    const slideIndex = Number.parseInt(selectedSegment.dataset.slideIndex || '', 10);
    if (Number.isNaN(slideIndex)) return;

    this.refs.slideshow?.select?.(slideIndex, event, { animate: false });
    this.#syncFitPaginationWithSlides(slideIndex);
  };

  #getAllSlideshowSlides() {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return [];

    return slideshow.refs?.slides ? Array.from(slideshow.refs.slides) : Array.from(slideshow.querySelectorAll('slideshow-slide'));
  }

  #getMobileSlideLimit() {
    const rawLimit = this.dataset.mobileSlideLimit || this.refs.slideshow?.dataset.mobileSlideLimit || '4';
    const parsedLimit = Number.parseInt(rawLimit, 10);

    if (Number.isNaN(parsedLimit)) return 4;

    return Math.min(Math.max(parsedLimit, 1), 6);
  }

  #applyMobileSlideLimit() {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return 0;

    if (this.dataset.mobileSlideLimit) {
      slideshow.dataset.mobileSlideLimit = this.dataset.mobileSlideLimit;
    }

    const allSlides = this.#getAllSlideshowSlides();
    if (!allSlides.length) return 0;

    allSlides.forEach((slide) => {
      if (slide.dataset.mobileLimitHidden !== 'true') return;
      slide.removeAttribute('hidden');
      slide.removeAttribute('data-mobile-limit-hidden');
    });

    allSlides.forEach((slide, index) => {
      if (slide.dataset.mobilePreviewHidden !== 'true') return;

      if (!mediaQueryLarge.matches) {
        slide.removeAttribute('hidden');
        return;
      }

      // Restore desktop behavior: only the first variant-attached slide stays visible by default.
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });

    if (!mediaQueryLarge.matches) {
      const mobileSlideLimit = this.#getMobileSlideLimit();
      let visibleSlideCount = 0;

      allSlides.forEach((slide) => {
        if (slide.hasAttribute('hidden')) return;

        visibleSlideCount += 1;
        if (visibleSlideCount <= mobileSlideLimit) return;

        slide.setAttribute('hidden', '');
        slide.setAttribute('aria-hidden', 'true');
        slide.dataset.mobileLimitHidden = 'true';
      });
    }

    const visibleSlides = slideshow.slides || [];
    const nextIndex = visibleSlides.length ? Math.min(slideshow.current, visibleSlides.length - 1) : 0;

    requestAnimationFrame(() => {
      if (!this.isConnected || !this.refs.slideshow) return;
      if (this.refs.slideshow.slides?.length) {
        this.refs.slideshow.select(nextIndex, undefined, { animate: false });
      }
      this.#syncFitPaginationWithSlides(nextIndex);
    });

    return visibleSlides.length;
  }

  #syncFitPaginationWithSlides(activeIndex = this.refs.slideshow?.current || 0) {
    const slideCount = this.refs.slideshow?.slides?.length || 0;

    this.#fitPaginationSegments.forEach((segment, index) => {
      const isVisible = index < slideCount;
      const isActive = isVisible && index === activeIndex;
      segment.hidden = !isVisible;
      segment.disabled = !isVisible;
      segment.classList.toggle('is-active', isActive);
      segment.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const image = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!image) return;
    // `sizes="auto, ..."` is only valid on lazy-loaded images. Dropping the
    // loading attribute without fixing sizes invalidates the whole attribute,
    // and the browser falls back to 100vw — which fetched the hover image at
    // width=3840 (3-4MB) for a 348px card. Strip the auto prefix first.
    const sizes = image.getAttribute('sizes');
    if (sizes && /^\s*auto\s*,/i.test(sizes)) {
      image.setAttribute('sizes', sizes.replace(/^\s*auto\s*,\s*/i, ''));
    }
    image.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    this.removeEventListener('pointerleave', this.#cancelFetchProductPagePrefetch);
    this.removeEventListener('focusout', this.#cancelFetchProductPagePrefetch);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
      // Cancel a pending prefetch if the pointer/focus leaves before the
      // debounce fires — this is what actually protects against "mouse sweeps
      // across the grid" and keeps us under Shopify's 429 limit.
      this.addEventListener('pointerleave', this.#cancelFetchProductPagePrefetch);
      this.addEventListener('focusout', this.#cancelFetchProductPagePrefetch);
    }

    this.#applyMobileSlideLimit();
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.syncProductUrls(anchorElement.href);
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    this.#syncFitPaginationWithSlides(event.detail.index);
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.hasAttribute('quick-add-active')) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.hasAttribute('quick-add-active')) return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    if (this.hasAttribute('quick-add-active')) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.#previousSlideIndex = null;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - reset to the first slide
    slideshow.select(0, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  #getActiveCollectionChildBreadcrumb() {
    const parentUrl = this.dataset.breadcrumbUrl || this.refs.productCardLink?.dataset.breadcrumbUrl || '';
    if (!parentUrl) return { childTitle: '', childUrl: '' };

    const currentPath = window.location.pathname.replace(/\/+$/, '');
    const normalizedParentPath = new URL(parentUrl, window.location.origin).pathname.replace(/\/+$/, '');
    const childPathPrefix = `${normalizedParentPath}/`;

    if (!currentPath.startsWith(childPathPrefix)) {
      return { childTitle: '', childUrl: '' };
    }

    const childHandle = currentPath.slice(childPathPrefix.length).split('/')[0];
    if (!childHandle) return { childTitle: '', childUrl: '' };

    const activeLink = document.querySelector(
      `[data-subcollection-filter-link][href$="${CSS.escape(`/${childHandle}`)}"]`
    );
    const childTitle =
      (activeLink instanceof HTMLAnchorElement ? activeLink.textContent?.trim() || '' : '') ||
      this.#getCollectionChildTitleFromHandle(childHandle);
    const childUrl = `${normalizedParentPath}/${childHandle}`;

    return {
      childTitle,
      childUrl,
    };
  }

  #getCollectionChildTitleFromHandle(childHandle) {
    if (!childHandle) return '';

    if (childHandle.includes('chino')) {
      return 'Chinos';
    }

    return childHandle
      .split('-')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  getBreadcrumbContext() {
    const title = this.dataset.breadcrumbTitle || this.refs.productCardLink?.dataset.breadcrumbTitle || '';
    const url = this.dataset.breadcrumbUrl || this.refs.productCardLink?.dataset.breadcrumbUrl || '';
    const derivedChildBreadcrumb = this.#getActiveCollectionChildBreadcrumb();
    const childTitle =
      this.dataset.breadcrumbChildTitle ||
      this.refs.productCardLink?.dataset.breadcrumbChildTitle ||
      derivedChildBreadcrumb.childTitle ||
      '';
    const childUrl =
      this.dataset.breadcrumbChildUrl ||
      this.refs.productCardLink?.dataset.breadcrumbChildUrl ||
      derivedChildBreadcrumb.childUrl ||
      '';
    const activeSwatch = this.querySelector('.product-recommendations__swatch-link.is-active[data-title-text]');
    const visibleProductTitle =
      this.refs.productTitleLink instanceof HTMLElement ? this.refs.productTitleLink.textContent?.trim() : '';
    const currentTitle =
      activeSwatch?.dataset.titleText?.trim() ||
      visibleProductTitle ||
      '';

    if (!title || !url) return null;

    return { title, url, childTitle, childUrl, currentTitle };
  }

  withBreadcrumbParams(urlValue) {
    if (!urlValue) return '';

    const breadcrumbContext = this.getBreadcrumbContext();
    if (!breadcrumbContext) return urlValue;

    const url = new URL(urlValue, window.location.origin);
    url.searchParams.set(ProductCard.breadcrumbTitleParam, breadcrumbContext.title);
    url.searchParams.set(ProductCard.breadcrumbUrlParam, breadcrumbContext.url);
    if (breadcrumbContext.childTitle && breadcrumbContext.childUrl) {
      url.searchParams.set(ProductCard.breadcrumbChildTitleParam, breadcrumbContext.childTitle);
      url.searchParams.set(ProductCard.breadcrumbChildUrlParam, breadcrumbContext.childUrl);
    } else {
      url.searchParams.delete(ProductCard.breadcrumbChildTitleParam);
      url.searchParams.delete(ProductCard.breadcrumbChildUrlParam);
    }
    if (breadcrumbContext.currentTitle) {
      url.searchParams.set(ProductCard.breadcrumbCurrentTitleParam, breadcrumbContext.currentTitle);
    } else {
      url.searchParams.delete(ProductCard.breadcrumbCurrentTitleParam);
    }
    return url.toString();
  }

  syncProductUrls(urlValue) {
    const nextUrl = this.withBreadcrumbParams(urlValue);
    if (!nextUrl) return '';

    const { productCardLink, productTitleLink, cardGalleryLink, quickAdd } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = nextUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = nextUrl;
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = nextUrl;
    }
    if (quickAdd) {
      quickAdd.dataset.productUrl = nextUrl;
    }

    return nextUrl;
  }

  syncBreadcrumbHref(link) {
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;

    const nextHref = this.withBreadcrumbParams(link.href);
    if (nextHref) {
      link.href = nextHref;
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.syncProductUrls(currentUrl.toString());
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = this.parentProductCard.withBreadcrumbParams(url.href);

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
